import { useEffect, useState, useCallback } from "react";
import { TrendingUp, Loader2, Info, Save, Sun, ChevronDown, ChevronUp, Flame, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  ComposedChart, Area, Line, ReferenceLine, ReferenceDot,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import api from "@/lib/api";
import SafeBoundary from "@/components/SafeBoundary";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(n || 0);

/**
 * Wirtschaftlichkeitsberechnung – 15-Jahres-Lifecycle-Vergleich
 * alter Brennstoff vs. Wärmepumpe. Wird unter dem KfW-458-Block angezeigt.
 * Stored on the customer record (customer.economics) so all offers share it.
 */
export default function WirtschaftlichkeitBlock({ projectId, previewGross = 0, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);
  const [tableOpen, setTableOpen] = useState(false);
  // Debounce previewGross changes so we don't hammer the backend while the
  // user fiddles with sliders/inputs upstream (e.g. discount field).
  const [debouncedGross, setDebouncedGross] = useState(previewGross);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedGross(previewGross), 350);
    return () => clearTimeout(t);
  }, [previewGross]);
  // Live KfW-Preview: der Kfw458Block feuert bei jeder Änderung ein
  // CustomEvent mit dem aktuell berechneten Eigenanteil. Wir übernehmen den
  // Wert (debounced) und schicken ihn als preview_eigenanteil an /economics
  // — damit die Amortisationskurve unten sofort mitzieht.
  const [debouncedEigenanteil, setDebouncedEigenanteil] = useState(null);
  useEffect(() => {
    let timer = null;
    const handler = (e) => {
      const d = e.detail || {};
      if (d.projectId !== projectId) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setDebouncedEigenanteil(d.eigenanteil), 350);
    };
    window.addEventListener("novatus:kfw-preview-change", handler);
    return () => {
      window.removeEventListener("novatus:kfw-preview-change", handler);
      if (timer) clearTimeout(timer);
    };
  }, [projectId]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (debouncedGross > 0) params.preview_gross = debouncedGross;
      if (debouncedEigenanteil !== null && debouncedEigenanteil !== undefined) {
        params.preview_eigenanteil = debouncedEigenanteil;
      }
      const r = await api.get(`/projects/${projectId}/economics`, { params });
      setData(r.data);
      // Iter88zy (2026-02-19) — pending NICHT bei jedem Reload überschreiben:
      // sonst gehen Benutzer-Änderungen (PV-Schalter, Preissteigerungs-Slider,
      // Notizen) verloren, weil der KfW-Block bei jeder Interaktion einen
      // preview_eigenanteil-Broadcast auslöst, der `load()` neu triggert.
      // Ergebnis vorher: `dirty` sofort wieder auf `false` → „Einstellungen
      // speichern"-Button nie klickbar. Fix: pending nur beim ersten Laden
      // vom Server übernehmen, sonst die lokalen Änderungen belassen.
      setPending((prev) => prev ?? r.data.settings);
    } catch {
      toast.error("Wirtschaftlichkeit konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [projectId, debouncedGross, debouncedEigenanteil]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/projects/${projectId}/economics`, pending);
      setData(r.data);
      setPending(r.data.settings);
      toast.success("Wirtschaftlichkeit aktualisiert");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 text-zinc-500 text-sm flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} /> Lade Wirtschaftlichkeit…
      </div>
    );
  }
  if (!data) return null;

  const c = data.calculation;
  const dirty = pending && (
    pending.pv_available !== data.settings.pv_available ||
    (pending.notes || "") !== (data.settings.notes || "") ||
    (pending.fuel_price_growth_pct ?? null) !== (data.settings.fuel_price_growth_pct ?? null)
  );

  return (
    <div
      className="bg-gradient-to-br from-[#0F1E18] to-[#0F0F12] border border-[#00E575]/25 rounded-2xl p-5 space-y-4"
      data-testid="economics-block"
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#00E575]/15 border border-[#00E575]/40 flex items-center justify-center flex-shrink-0">
          <TrendingUp size={20} className="text-[#00E575]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base font-semibold leading-tight">Wirtschaftlichkeit · 15-Jahres-Vergleich</h3>
          <p className="text-[12px] text-[#00E575]/80 font-mono uppercase tracking-wider mt-0.5">
            Alter Brennstoff vs. Wärmepumpe · CO₂-Pfad BEHG/ETS-II
          </p>
        </div>
      </div>

      {/* PV-Schalter */}
      <ToggleRow
        testid="econ-pv"
        icon={<Sun size={16} className="text-[#00E575]" />}
        label="PV-Anlage vorhanden"
        pct="−17,5 %"
        hint="Reduziert den WP-Strompreis um 17,5 % (PV-Eigenverbrauch)"
        checked={!!pending?.pv_available}
        onChange={() => setPending({ ...pending, pv_available: !pending.pv_available })}
      />

      {/* Brennstoff-Preissteigerungs-Slider */}
      <FuelGrowthSlider
        valuePct={pending?.fuel_price_growth_pct}
        defaultPct={6}
        onChange={(v) => setPending({ ...pending, fuel_price_growth_pct: v })}
      />

      {/* Notes */}
      <div>
        <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1.5 font-bold">
          Hinweise zur Wirtschaftlichkeit (intern)
        </label>
        <textarea
          rows={2}
          value={pending?.notes || ""}
          onChange={(e) => setPending({ ...pending, notes: e.target.value })}
          placeholder="z. B. Bestands-Heizung, kalkulatorische Annahmen"
          className="w-full bg-[#09090B] border border-zinc-700 rounded-xl p-2.5 text-sm text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none resize-y"
          data-testid="econ-notes"
        />
      </div>

      {/* Calculation */}
      {!c.applicable ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs text-zinc-400 flex items-start gap-2">
          <Info size={14} className="text-zinc-500 mt-0.5 flex-shrink-0" />
          <span>{c.reason || "Wirtschaftlichkeit nicht berechenbar."}</span>
        </div>
      ) : c.no_investment ? (
        <div className="bg-amber-500/5 border border-amber-500/40 rounded-xl p-4 text-xs text-amber-300/90 flex items-start gap-2" data-testid="econ-no-investment">
          <Info size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <span>
            <b className="text-amber-200">Investitionsbasis noch 0 €.</b> Bitte
            zuerst ein Paket auswählen und <i>„Angebot erstellen &amp; PDF generieren"</i>
            anklicken – danach rechnet die 15-Jahres-Bilanz korrekt mit Brutto-Investition
            bzw. Eigenanteil nach KfW.
          </span>
        </div>
      ) : (
        <>
          {/* Investitions-Box */}
          <div className="bg-[#09090B] border border-zinc-800 rounded-xl p-4 space-y-1.5">
            <Row label="Jahresverbrauch alt" value={`${Number(c.annual_kwh || 0).toLocaleString("de-DE")} kWh ${c.fuel === "gas" ? "Gas" : c.fuel === "oil" ? "Öl" : c.fuel || ""}`} />
            <Row label={`Investitionsbasis (${c.investment?.basis || "—"})`} value={fmtEur(c.investment?.eur)} />
            <Row label={`Ø Stromkosten WP${c.pv_available ? " (mit PV)" : ""}`} value={`${Number(c.assumptions?.hp_base_electricity_price_per_kwh || 0).toFixed(3)} €/kWh`} />
          </div>

          {/* 15y-Summary */}
          <div className="bg-[#09090B] border border-zinc-800 rounded-xl p-4 space-y-1.5">
            <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-bold mb-2">
              Bilanz über 15 Jahre
            </div>
            <Row label="Kosten alte Heizung (15 J.)" value={fmtEur(c.totals_15y?.old_cost_eur)} />
            <Row label="Kosten Wärmepumpe (15 J.)" value={fmtEur(c.totals_15y?.new_cost_eur)} />
            <div className="border-t border-zinc-800 my-2" />
            <Row label="Ersparnis Betriebskosten" value={fmtEur(c.totals_15y?.savings_eur)} green />
            <Row label={`Netto nach Invest (${fmtEur(c.investment?.eur)})`} value={fmtEur(c.totals_15y?.net_savings_after_invest_eur)} green bold />
            <Row label="CO₂-Ersparnis insgesamt" value={`${Number(c.totals_15y?.co2_savings_tonnes || 0).toLocaleString("de-DE")} t`} />
            <div className="border-t border-zinc-800 my-2" />
            <Row
              label="Amortisation"
              value={c.payback_year_index ? `nach ${c.payback_year_index} Jahren` : "länger als 15 J."}
              bold
            />
            {c.payback_years_decimal != null && (
              <Row label="Statische Amortisation (Ø)" value={`${String(c.payback_years_decimal).replace(".", ",")} Jahre`} dim />
            )}
          </div>

          {/* Liniendiagramm: kumulierte Ersparnis vs. Investition */}
          {Array.isArray(c.years_table) && c.years_table.length > 0 && (
            <SafeBoundary>
              <AmortizationChart calculation={c} />
            </SafeBoundary>
          )}

          {/* Klappbare Jahres-Tabelle */}
          <button
            onClick={() => setTableOpen(!tableOpen)}
            data-testid="econ-table-toggle"
            className="w-full flex items-center justify-between text-[12px] text-zinc-400 hover:text-zinc-200 transition py-2 font-medium"
          >
            <span className="font-bold uppercase tracking-wider">
              Detail-Tabelle · 15 Jahre
            </span>
            {tableOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {tableOpen && (
            <div className="bg-[#09090B] border border-zinc-800 rounded-xl overflow-x-auto" data-testid="econ-detail-table">
              <table className="w-full text-[12px]">
                <thead className="bg-zinc-900 text-zinc-500 font-mono uppercase">
                  <tr>
                    <th className="py-2 px-2 text-left">Jahr</th>
                    <th className="py-2 px-2 text-right">Alt €</th>
                    <th className="py-2 px-2 text-right">Neu €</th>
                    <th className="py-2 px-2 text-right">Ersparnis</th>
                    <th className="py-2 px-2 text-right">Kum.</th>
                    <th className="py-2 px-2 text-right">CO₂ €/t</th>
                  </tr>
                </thead>
                <tbody>
                  {(c.years_table || []).map((y) => (
                    <tr key={y.year} className={`border-t border-zinc-800 ${c.payback_year_index === y.offset ? "bg-[#00E575]/10" : ""}`}>
                      <td className="py-1.5 px-2 font-mono text-zinc-300">{y.year}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-zinc-400">{Number(y.old_total_eur || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-zinc-400">{Number(y.new_total_eur || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-emerald-300 font-semibold">{Number(y.savings_eur || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-zinc-300">{Number(y.cum_savings_eur || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-zinc-500">{String(y.co2_price_per_t ?? "").replace(".", ",")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Annahmen */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 text-[11.5px] text-zinc-200 leading-relaxed font-semibold">
            <div className="font-semibold text-emerald-300 uppercase tracking-wider text-[10.5px] mb-1.5">Kalkulations-Annahmen</div>
            • Brennstoff-Preissteigerung +{String(c.assumptions?.fuel_price_growth_pct ?? "").replace(".", ",")} %/Jahr ·
            Strompreis +{String(c.assumptions?.electricity_price_growth_pct ?? "").replace(".", ",")} %/Jahr ·
            CO₂: {c.assumptions?.co2_path || "—"} ·
            JAZ {c.assumptions?.hp_jaz || "—"}
            {c.pv_available && <> · PV-Bonus −{String(c.assumptions?.pv_reduction_pct ?? "").replace(".", ",")} %</>}
          </div>
        </>
      )}

      <button
        data-testid="econ-save-btn"
        onClick={save}
        disabled={!dirty || saving}
        className="h-11 w-full rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#00C964] active:scale-[0.97] transition flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} /> Einstellungen speichern</>}
      </button>

      <p className="text-[11.5px] text-zinc-300 font-semibold leading-relaxed">
        <span className="text-[#00E575]">Hinweis:</span> Unverbindliche Modellrechnung. Tatsächliche Einsparungen hängen von Nutzungsverhalten, Marktpreisen und Förderpfad ab. CO₂-Preise nach BEHG (bis 2030) und geschätztem ETS-II-Pfad (ab 2031).
      </p>
    </div>
  );
}

function FuelGrowthSlider({ valuePct, defaultPct, onChange }) {
  const isOverridden = valuePct !== null && valuePct !== undefined;
  const effective = isOverridden ? Number(valuePct) : defaultPct;
  const fmt = (v) => `+${String(v).replace(".", ",")} %`;
  const presets = [5, 6, 8, 10, 12];
  return (
    <div
      className={`rounded-xl border p-3 transition ${
        isOverridden ? "bg-[#00E575]/5 border-[#00E575]/40" : "bg-[#0E0E12] border-zinc-800"
      }`}
      data-testid="econ-fuel-growth"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
          <Flame size={16} className={isOverridden ? "text-[#00E575]" : "text-zinc-400"} />
          Brennstoff-Preissteigerung
        </span>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-[13px] font-semibold tabular-nums ${
            isOverridden ? "text-emerald-300" : "text-zinc-300"
          }`}>
            {fmt(effective)}<span className="text-[12px] text-zinc-300 ml-1">/Jahr</span>
          </span>
          {isOverridden && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="h-7 w-7 rounded-md bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
              title="Auf Default (6 %) zurücksetzen"
              data-testid="econ-fuel-growth-reset"
            >
              <RotateCcw size={11} className="text-zinc-400" />
            </button>
          )}
        </div>
      </div>
      <div className="text-[12px] text-zinc-300 leading-snug mb-2">
        Default <b className="text-zinc-300">+6 %/Jahr</b> (BMWK-Mittelwert).
        Slider zwischen 3 % (sehr konservativ) und 15 % (Worst-Case) anpassbar.
      </div>
      <input
        type="range"
        min={3}
        max={15}
        step={0.5}
        value={effective}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-[#00E575] h-2"
        data-testid="econ-fuel-growth-range"
      />
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p === defaultPct ? null : p)}
            className={`px-2 py-0.5 rounded-md text-[10.5px] font-mono font-bold transition ${
              effective === p
                ? "bg-[#00E575] text-[#09090B]"
                : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
            }`}
            data-testid={`econ-fuel-preset-${p}`}
          >
            +{p} %
          </button>
        ))}
      </div>
    </div>
  );
}

function ToggleRow({ testid, label, pct, hint, checked, onChange, icon }) {
  return (
    <button
      type="button"
      onClick={onChange}
      data-testid={testid}
      className={`w-full text-left rounded-xl border px-3 py-2.5 flex items-start gap-3 transition ${
        checked ? "bg-[#00E575]/10 border-[#00E575]/40" : "bg-[#0E0E12] border-zinc-800 hover:border-zinc-700"
      }`}
    >
      <div className={`mt-0.5 h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
        checked ? "bg-[#00E575] border-[#00E575]" : "border-zinc-600"
      }`}>
        {checked && <span className="text-[#09090B] text-xs font-bold">✓</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            {icon}{label}
          </span>
          <span className={`font-mono text-xs font-semibold ${checked ? "text-emerald-300" : "text-zinc-300"}`}>{pct}</span>
        </div>
        <div className="text-[12px] text-zinc-300 mt-0.5 leading-snug">{hint}</div>
      </div>
    </button>
  );
}

function Row({ label, value, green, bold, dim }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={`text-xs ${dim ? "text-zinc-300" : "text-zinc-400"}`}>{label}</span>
      <span className={`font-mono text-xs ${
        green && bold ? "text-emerald-300 font-semibold text-sm" :
        green ? "text-emerald-300 font-medium" :
        bold ? "text-zinc-100 font-semibold" :
        dim ? "text-zinc-500" : "text-zinc-200"
      }`}>{value}</span>
    </div>
  );
}

function AmortizationChart({ calculation }) {
  const c = calculation || {};
  const yt = Array.isArray(c.years_table) ? c.years_table : [];
  if (yt.length === 0) return null;
  const investEur = Number(c.investment?.eur || 0);
  const data = yt.map((y) => ({
    year: Number(y.year || 0),
    offset: Number(y.offset || 0),
    cum: Number(y.cum_savings_eur || 0),
    invest: investEur,
  }));
  const payback = Number(c.payback_year_index || 0);
  const paybackRow = payback && data[payback - 1] ? data[payback - 1] : null;

  const fmtCompact = (n) => {
    try {
      return new Intl.NumberFormat("de-DE", { notation: "compact", maximumFractionDigits: 1 }).format(Number(n) || 0) + " €";
    } catch {
      return `${Math.round(Number(n) || 0)} €`;
    }
  };
  const fmtCurrency = (n) => {
    try {
      return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
    } catch {
      return `${Math.round(Number(n) || 0)} €`;
    }
  };

  return (
    <div className="bg-[#09090B] border border-zinc-800 rounded-xl p-3" data-testid="econ-chart">
      <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-bold mb-2 px-1">
        Kumulierte Ersparnis im Zeitverlauf
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
            <defs>
              <linearGradient id="econGreen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00E575" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#00E575" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#27272A" strokeDasharray="2 2" vertical={false} />
            <XAxis
              dataKey="year"
              stroke="#71717A"
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              tickFormatter={(v, i) => (i === 0 || i === data.length - 1 || i % 3 === 0 ? v : "")}
              axisLine={{ stroke: "#3F3F46" }}
              tickLine={false}
            />
            <YAxis
              stroke="#71717A"
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              tickFormatter={fmtCompact}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#18181B",
                border: "1px solid #3F3F46",
                borderRadius: 8,
                fontSize: 11,
              }}
              labelStyle={{ color: "#A1A1AA", fontWeight: 600, marginBottom: 2 }}
              itemStyle={{ color: "#E4E4E7" }}
              formatter={(v, name) => [
                fmtCurrency(v),
                name === "cum" ? "Kumulierte Ersparnis" : "Investition",
              ]}
              labelFormatter={(label) => `Jahr ${label}`}
            />
            <Legend
              wrapperStyle={{ fontSize: 10, color: "#A1A1AA", paddingTop: 4 }}
              iconType="line"
              formatter={(v) => v === "cum" ? "Kumulierte Ersparnis" : "Investition"}
            />
            <Area
              type="monotone"
              dataKey="cum"
              stroke="#00E575"
              strokeWidth={2}
              fill="url(#econGreen)"
              dot={{ fill: "#00E575", stroke: "#0F0F12", strokeWidth: 1, r: 2.5 }}
              activeDot={{ r: 4, stroke: "#fff", strokeWidth: 1 }}
              name="cum"
            />
            <Line
              type="monotone"
              dataKey="invest"
              stroke="#71717A"
              strokeWidth={1.4}
              strokeDasharray="4 3"
              dot={false}
              activeDot={false}
              name="invest"
            />
            {paybackRow && (
              <ReferenceDot
                x={paybackRow.year}
                y={paybackRow.cum}
                r={6}
                fill="#00E575"
                stroke="#fff"
                strokeWidth={2}
                isFront
                label={{
                  value: `Amortisation Jahr ${payback}`,
                  position: "top",
                  fill: "#00E575",
                  fontSize: 10,
                  fontWeight: 700,
                  offset: 8,
                }}
              />
            )}
            {c.investment?.eur > 0 && (
              <ReferenceLine
                y={investEur}
                stroke="transparent"
                label={{
                  value: `Investition ${fmtCurrency(investEur)}`,
                  position: "insideTopRight",
                  fill: "#A1A1AA",
                  fontSize: 9,
                  fontWeight: 600,
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
